/******************************************************************************
 *
 * Copyright (c) 2019-2022 ServiceActivator Inc. All Rights Reserved.
 *
 * Unauthorized copying of the code in this file, completely or partially,
 * via any medium is strictly prohibited.
 *
 * Proprietary and Confidential.
 *
 * Written by 'Shivam Mishra' <shivam.mishra@wakencode.com>, June 2022
 *
 *****************************************************************************/

import React, { useEffect, useState } from "react";
import NumberFormat from "react-number-format";
import { verifyUserMobileStatus } from "../../network";
import { string } from "../../constant/string";
import Loader from "../../loader/WhiteLoader";
import Styles from "./SignUp.module.css";

/**
 * Sign up page called in Wrapper.js
 * This page takes user mobile number and save
 * them to local storage
 *
 * @param {String} setPage State for current page
 * @param {String} setJobRole State for job role
 * @returns JSX for sign-up page
 */
const SignUp = ({ setPage, setJobRole }) => {
  // State for mobile number
  const [phoneNumber, setPhoneNumber] = useState(null);
  // State for error is user enter invalid input
  const [inputError, setInputError] = useState(null);
  // State for loader
  const [loading, setLoading] = useState(false);

  /**
   * Update user mobile number on screen from local storage
   */
  useEffect(() => {
    // Get user mobile number from local storage
    let mobileNumberFromLocalStorage = localStorage.getItem("mobileNumber");
    if (mobileNumberFromLocalStorage) {
      let newMobileNumber =
        mobileNumberFromLocalStorage.substring(0, 2) +
        " " +
        mobileNumberFromLocalStorage.substring(2, 5) +
        " " +
        mobileNumberFromLocalStorage.substring(5, 8) +
        " " +
        mobileNumberFromLocalStorage.substring(8, mobileNumberFromLocalStorage.length);
      // Set state for mobile number
      setPhoneNumber(newMobileNumber);
    }
  }, []);

  /**
   * Save/update mobile number in local storage
   *
   * @param {String} mobile Mobile number entered by user
   */
  const saveMobileNumberToLocalStorage = (mobileNumber) => {
    let mobileNumberFromLocalStorage = localStorage.getItem("mobileNumber");
    if (mobileNumberFromLocalStorage) {
      // Clear local storage if user enter with diffrent mobile number
      if (mobileNumberFromLocalStorage !== mobileNumber) {
        localStorage.clear();
      }
    }
    // Save mobile number in local storage
    localStorage.setItem("mobileNumber", mobileNumber);
  };

  /**
   * Call api to check status of user mobile number
   *
   * @param {String} mobileNumberWithoutPrefix Mobile number without prefix(Country code)
   */
  const callApiToVerifyUserMobile = (mobileNumberWithoutPrefix) => {
    // Prepare correct format for request body
    let postData = {
      mobile: `+1${mobileNumberWithoutPrefix}`,
    };

    // Call api to verify user mobile
    verifyUserMobileStatus(postData)
      .then(() => {
        // Save mobile number to local storage
        saveMobileNumberToLocalStorage(phoneNumber.replace(/ /g, ""));
        // Update current page to Verification page
        setPage("Verification");
        setLoading(false);
      })
      .catch((err) => {
        if (err.response && err.response.data && err.response.data.errorDescription) {
          // Update error state
          setInputError(string.MOBILE_ALREADY_USED);
        }
        setLoading(false);
      });
  };

  /**
   * Next button handler
   *
   * @returns {void}
   */
  const handleClick = () => {
    setJobRole(null);
    // If user not entered mobile number update error state and return
    if (!phoneNumber) {
      setInputError(string.REQUIRED);
      return;
    }

    let mobileNumberWithoutPrefix = phoneNumber.substring(2, phoneNumber.length).replace(/ /g, "");
    // If user entered invalid mobile number update error state and return
    if (mobileNumberWithoutPrefix.length > 0 && mobileNumberWithoutPrefix.length < 10) {
      setInputError(string.NUMBER_MUST_BE_10_DIGIT);
      return;
    }
    setLoading(true);
    // Create user account
    callApiToVerifyUserMobile(mobileNumberWithoutPrefix);
  };

  /**
   * Input handle change
   *
   * @param {*} e Input change event
   */
  const handleMobileNumberChnage = (e) => {
    setInputError(null);
    // Set state for mobile number
    setPhoneNumber(e.target.value);
  };

  // handle click on 'Enter' button
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleClick();
    }
  };

  return (
    <div className="main">
      {/* Sign up head text */}
      <div className="headText">{string.SIGN_UP}</div>
      {/* Line under head text */}
      <div className="line"></div>
      {/* Sub-heading text under line */}
      <div className="text">{string.PLEASE_ENTER_YOUR_PHONE_NUMBER_TO_CONTINUE}</div>
      {/* Input field for mobile number */}
      <div className="tenREM">
        <NumberFormat
          onChange={handleMobileNumberChnage}
          onKeyDown={(e) => handleKeyDown(e)}
          className={inputError ? "wrongInput" : "input"}
          value={phoneNumber}
          placeholder={string.PHONE_NUMBER}
          type="Tel"
          format="+1 ### ### ####"
        ></NumberFormat>
        {inputError ? <p className="errorText">{inputError}</p> : null}
      </div>
      {/* Term and condition text */}
      <div className={Styles.byClickText}>
        <p>{string.BY_CLICKING_NEXT}</p>
        <a
          className={Styles.termAndConditions}
          href={process.env.REACT_APP_TERMS_AND_CONDITIONS}
          target="_blank"
          rel="noreferrer"
        >
          {string.TERMS_CONDITIONS}
        </a>
      </div>
      {/* Next button */}
      <div>
        <button className="nextButton" onClick={loading === false ? handleClick : null}>
          {loading ? <Loader /> : string.NEXT}
        </button>
      </div>
      <div className={Styles.alreadyHaveAnAccount}>
        <a className={Styles.haveAnAccountText} href={process.env.REACT_APP_COMMUNITY_WEB_APP_URL}>
          {string.ALREADY_HAVE_AN_ACCOUNT}
        </a>
      </div>
    </div>
  );
};

export default SignUp;
